// ABOUTME: SPEC-53 — the stage a reel session is in (`reel` | `collection` | `piece`), driven by the
// ABOUTME: backend's `stage_command` messages; pure reducer + the App-facing hook around it.
//
// Wire contract (services/collection_stage.py module docstring):
//   enter_collection {collection:{key,title,image,price_header,slots[]}, transition}
//   open_piece       {collection_key, product_id, product: <normalised envelope>, transition}
//   close_piece      {collection_key}
//   return_to_reel   {chapter_id?, transition}
//
// Rules the reducer enforces (review R5 / R8 / §4.9):
//   • A `reel_command` while the stage is not the reel is applied ONLY if a `return_to_reel` is in
//     flight (it is queued and replayed after the return's own chapter — at the swap's commit, or
//     for a `fade_black` return once the black has fully lifted, SPEC-66); otherwise it is dropped
//     with a console.warn. Nothing is ever applied late.
//   • A fade swaps the stage while the black is opaque; every stage command that arrives while the
//     fade is darkening is queued and applied, in order, at that swap — so `enter_collection` +
//     `open_piece` in one turn land together under the black.
//   • `transition: "none"` (a reconnect restore) applies immediately, no fade.
//   • The card owns its gallery index; `switch_gallery_image` / `gallery_navigate` act on it here.
//
// SPEC-59 D-10 (film gate): while an intro film owns the screen a stage command is HELD, never
// applied. WHY: the stage host paints over the film and `reelOffStage` then pauses both video
// surfaces (Projectionist/index.js), and a paused element never reaches `ended` — so the film never
// reports finishing, `intro_finished` is never sent and the guest is greeted by silence on a green
// socket. The gate has its OWN queue (`pendingFilmCommands`), separate from the fade's
// `pendingStageCommands`, because the fade's 450 ms timer is a second, command-less route to a
// non-REEL stage: a fade that began before the film rose would otherwise drain its queue under it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { FadePhase } from './useStageTransition';
import { imagesOf } from './pieceEnvelope';

export const Stage = Object.freeze({ REEL: 'reel', COLLECTION: 'collection', PIECE: 'piece' });

export const initialStageState = Object.freeze({
  stage: Stage.REEL,
  collectionKey: null,
  collection: null,        // the `enter_collection` descriptor
  productId: null,
  product: null,           // the `open_piece` envelope
  galleryIndex: 0,
  returning: false,        // a return_to_reel has been received and not yet swapped
  pendingReelCommands: [], // reel_commands received during that return
  pendingStageCommands: [], // stage_commands received while a fade is darkening
  pendingFilmCommands: [], // SPEC-59 D-10: stage_commands held because a film owns the screen
});

const isFade = (msg) => msg?.transition === 'fade_black';

/**
 * Apply one stage command. Pure. Returns the next state and the reel commands the caller must
 * hand to the Projectionist NOW (only a return produces any).
 *
 * @returns {{ state: object, replay: object[] }}
 */
export function applyStageCommand(state, msg) {
  switch (msg?.action) {
    case 'enter_collection': {
      const collection = msg.collection || null;
      return {
        state: {
          ...state,
          stage: Stage.COLLECTION,
          collectionKey: collection?.key ?? msg.collection_key ?? null,
          collection,
          productId: null,
          product: null,
          galleryIndex: 0,
        },
        replay: [],
      };
    }
    case 'open_piece': {
      const sameProduct = state.productId != null && state.productId === msg.product_id;
      return {
        state: {
          ...state,
          stage: Stage.PIECE,
          collectionKey: msg.collection_key ?? state.collectionKey,
          productId: msg.product_id ?? msg.product?.id ?? null,
          product: msg.product || null,
          // SPEC-53 (owner 2026-09-09): a new piece always opens on its first photo; the backend
          // says so explicitly (`gallery_index`), the same-product case keeps the guest's place.
          galleryIndex: Number.isInteger(msg.gallery_index) ? msg.gallery_index
            : (sameProduct ? state.galleryIndex : 0),
        },
        replay: [],
      };
    }
    case 'close_piece': {
      if (state.stage !== Stage.PIECE) return { state, replay: [] };
      return { state: { ...state, stage: Stage.COLLECTION, productId: null, product: null, galleryIndex: 0 }, replay: [] };
    }
    case 'return_to_reel': {
      const queued = state.pendingReelCommands;
      const alreadyPlaysChapter = msg.chapter_id
        && queued.some((c) => c.action === 'play_chapter' && c.chapter_id === msg.chapter_id);
      const replay = [
        ...(msg.chapter_id && !alreadyPlaysChapter
          ? [{ type: 'reel_command', action: 'play_chapter', chapter_id: msg.chapter_id }] : []),
        ...queued,
      ];
      return {
        state: {
          ...state,
          stage: Stage.REEL,
          collectionKey: null,
          collection: null,
          productId: null,
          product: null,
          galleryIndex: 0,
          returning: false,
          pendingReelCommands: [],
        },
        replay,
      };
    }
    default:
      console.warn('[SPEC-53] Unknown stage_command action:', msg?.action);
      return { state, replay: [] };
  }
}

/**
 * Where a `reel_command` goes right now. Pure.
 * @returns {{ state: object, verdict: 'apply'|'queued'|'dropped' }}
 */
export function routeReelCommand(state, msg) {
  if (state.stage === Stage.REEL) return { state, verdict: 'apply' };
  if (state.returning) {
    return { state: { ...state, pendingReelCommands: [...state.pendingReelCommands, msg] }, verdict: 'queued' };
  }
  console.warn('[SPEC-53] reel_command dropped — the reel is off stage and no return is in flight:', msg?.action);
  return { state, verdict: 'dropped' };
}

/**
 * Drain the fade's queue at the swap, in arrival order. Pure.
 *
 * SPEC-59 D-10 (ii): the swap runs off a 450 ms timer, not off a command, so a film can rise
 * between `begin()` and the timer firing. `filmUp` moves whatever is still queued into the film
 * gate's queue instead of applying it — the option is DEFAULTED so every existing caller and test
 * of the pure function is unchanged.
 *
 * ACCEPTED COSMETIC CASE, so a future reader does not read it as a bug: re-queueing here does not
 * cancel the fade, which lifts its own black on schedule — so this one interleaving (a stage
 * command, then a SPEC-43 `replay_intro` inside the following 450 ms) costs ~900 ms of black in
 * and out over the film with no stage change behind it. Left alone deliberately. Cancelling would
 * mean a new verb on `useStageTransition`, working code this spec's seam table does not name
 * (Standard §0), and it would snap from full black straight back to the film — a harder cut than
 * the symmetrical fade the guest gets today. The wrong outcome this function exists to prevent —
 * the stage mounting over the film and pausing it into permanent silence — is prevented either way.
 *
 * @param {{ filmUp?: boolean }} [opts]
 */
export function performSwap(state, { filmUp = false } = {}) {
  if (filmUp) {
    return {
      state: {
        ...state,
        pendingStageCommands: [],
        pendingFilmCommands: [...state.pendingFilmCommands, ...state.pendingStageCommands],
      },
      replay: [],
    };
  }
  let next = { ...state, pendingStageCommands: [] };
  const replay = [];
  for (const msg of state.pendingStageCommands) {
    const out = applyStageCommand(next, msg);
    next = out.state;
    replay.push(...out.replay);
  }
  return { state: next, replay };
}

/** Does a hydrated `stage` value already describe what is on screen (with its data present)? */
export function stageMatches(state, hydrated) {
  if (!hydrated) return state.stage === Stage.REEL;
  if (hydrated.stage !== state.stage) return false;
  if ((hydrated.collection ?? null) !== (state.collectionKey ?? null)) return false;
  if (!state.collection) return false;
  if (hydrated.stage === Stage.PIECE) {
    return !!state.product && (hydrated.piece_id ?? null) === (state.productId ?? null);
  }
  return true;
}

/**
 * The App-facing machine. State lives in a ref mirror as well as React state because App.js's
 * WebSocket handler is bound once per connection and closes over a stale render (the same reason
 * `viewRef` / `productImagesRef` exist there).
 *
 * SPEC-59 D-10 (iii): `filmActive` is the ONE source of truth for "a film owns the screen". It is
 * mirrored into the hook's own ref during render — the idiom already used for `onReelCommand` and
 * `fade` below — rather than read from App's `showIntroRef`, because the two skew on the reel
 * path's falling edge: `sendReelIntroFinished` writes only the state, and the ref is synced by an
 * effect registered after this hook, so at flush time the ref would still say "film up" and every
 * flushed command would re-queue and strand for the session.
 *
 * @param {{ onReelCommand: (msg: object) => void, fade: ReturnType<typeof import('./useStageTransition').useStageTransition>, filmActive?: boolean }} deps
 */
export function useStageMachine({ onReelCommand, fade, filmActive = false }) {
  const [state, setState] = useState(initialStageState);
  const stateRef = useRef(state);
  const onReelCommandRef = useRef(onReelCommand);
  onReelCommandRef.current = onReelCommand;
  const fadeRef = useRef(fade);
  fadeRef.current = fade;
  const filmRef = useRef(filmActive);
  filmRef.current = filmActive;

  const update = useCallback((fn) => {
    const next = fn(stateRef.current);
    stateRef.current = next;
    setState(next);
    return next;
  }, []);

  // Replays are handed to the Projectionist only AFTER the commit that renders `stage: reel`: it
  // reads its `offStage` prop during render (offStageRef) and refuses commands while that is true,
  // so a replay fired synchronously at the swap would be refused against the stale prop and the
  // chapter the backend asked for would be lost (AC-5; caught by the headless run, 2026-09-09).
  const pendingReplayRef = useRef([]);
  const restoreAskedRef = useRef(null);   // the hydrated stage a `stage_restore` was last sent for
  // SPEC-66 (client report 2026-09-25): a return that crossed the black holds its replay until the
  // fade has fully LIFTED, so the guest sees Vélos → black → the film at rest → then the chapter.
  // Released at the swap's commit (above), the chapter started under the lifting black — the
  // "sudden jump". null = not holding; an array = the commands waiting, in arrival order. Anything
  // that would replay while it is up (a later swap, a lightening-phase reel_command) joins behind.
  const liftHoldRef = useRef(null);
  const replayAll = useCallback((commands) => {
    if (liftHoldRef.current) { liftHoldRef.current = [...liftHoldRef.current, ...commands]; return; } // SPEC-66
    if (commands.length) pendingReplayRef.current = [...pendingReplayRef.current, ...commands];
  }, []);
  useEffect(() => {
    if (!pendingReplayRef.current.length) return;
    const commands = pendingReplayRef.current;
    pendingReplayRef.current = [];
    commands.forEach((cmd) => { try { onReelCommandRef.current?.(cmd); } catch (e) { console.error('[SPEC-53] replay failed', e); } });
  }, [state]);

  // SPEC-66: release once the fade reports neither leg running. `fade.phase` is the render-time
  // signal the real hook re-renders App with; the phaseRef read is what decides, so a fade that
  // exposes no phase at all releases at the commit exactly as before (never stuck).
  const fadePhase = fade?.phase;
  useEffect(() => {
    const held = liftHoldRef.current;
    if (!held) return;
    if (stateRef.current.stage !== Stage.REEL) {
      // The reel left the stage mid-lift: drop NOW, whatever the fade is doing, so a later return
      // starts a fresh hold instead of inheriting this stale chapter (review MEDIUM, 2026-09-25).
      liftHoldRef.current = null;
      if (held.length) console.warn(`[SPEC-66][REPLAY_DROPPED] commands=${held.length} stage=${stateRef.current.stage}`);
      return;
    }
    const phase = fadeRef.current?.phaseRef?.current;
    if (phase === FadePhase.DARKENING || phase === FadePhase.LIGHTENING) return;
    liftHoldRef.current = null;
    if (held.length) console.log(`[SPEC-66][REPLAY_RELEASED] commands=${held.length}`);
    held.forEach((cmd) => { try { onReelCommandRef.current?.(cmd); } catch (e) { console.error('[SPEC-66] replay failed', e); } });
  }, [state, fadePhase]);

  const swap = useCallback(() => {
    let replay = [];
    let returned = false;
    update((s) => {
      // SPEC-66: a return drained under the black (not re-queued for a film) holds for the lift.
      returned = !filmRef.current && s.pendingStageCommands.some((c) => c?.action === 'return_to_reel');
      const out = performSwap(s, { filmUp: filmRef.current }); replay = out.replay; return out.state;
    });
    if (returned && !liftHoldRef.current) liftHoldRef.current = [];
    replayAll(replay);
  }, [update, replayAll]);

  const handleStageCommand = useCallback((msg) => {
    if (!msg || !msg.action) return;
    // SPEC-53: a return in flight is what makes `routeReelCommand` QUEUE a `reel_command` instead
    // of dropping it as off-stage. SPEC-59 moves the declaration above the film gate so a HELD
    // return still declares it — otherwise a chapter arriving under the film, between the return
    // and its replay, is dropped, and the gate has no business causing that. The guard is the
    // other half: a held return is replayed through this same function, and the chapters queued
    // behind it while it waited belong to THIS return, so clearing them a second time would drop
    // exactly what the declaration was for. `applyStageCommand` puts `returning` back to false
    // when the return finally applies; `resetPending` and `hydrate(null)` clear it too.
    if (msg.action === 'return_to_reel') {
      update((s) => (s.returning ? s : { ...s, returning: true, pendingReelCommands: [] }));
    }
    // SPEC-59 D-10: a film owns the screen — hold the command and replay it through this same
    // function on the film's falling edge, so each still gets the treatment its `transition` asks.
    if (filmRef.current) {
      update((s) => ({ ...s, pendingFilmCommands: [...s.pendingFilmCommands, msg] }));
      return;
    }
    const darkening = fadeRef.current?.phaseRef?.current === FadePhase.DARKENING;
    if (isFade(msg) || darkening) {
      update((s) => ({ ...s, pendingStageCommands: [...s.pendingStageCommands, msg] }));
      fadeRef.current?.begin(swap);
      return;
    }
    let replay = [];
    update((s) => { const out = applyStageCommand(s, msg); replay = out.replay; return out.state; });
    replayAll(replay);
  }, [update, replayAll, swap]);

  // SPEC-59 D-10: the film's falling edge. `filmRef.current` was assigned during this same render,
  // before effects run, so a replayed command sees the film down and cannot re-queue itself.
  useEffect(() => {
    if (filmActive) return;
    const held = stateRef.current.pendingFilmCommands;
    if (!held.length) return;
    update((s) => ({ ...s, pendingFilmCommands: [] }));
    held.forEach((msg) => handleStageCommand(msg));
  }, [filmActive, update, handleStageCommand]);

  /**
   * SPEC-59 D-15: a new connection begins on the reel, holding nothing. Called from `ws.onopen`.
   *
   * WHY it resets the STAGE and not merely the queues. `useStageMachine`'s state lives in App,
   * which survives a WebSocket reconnect (SPEC-11 re-runs the connect effect off `reconnectNonce`;
   * nothing remounts), so after an in-place reconnect the screen still shows whatever collection
   * was up. The server does not: SPEC-59 P3's `arrival_reset` pops `stage`, so the envelope omits
   * it, so `applyHydration` never calls this hook's `hydrate` — which is what used to re-align the
   * two. Clearing only the queues would leave the frontend on Vélos and the backend on the reel
   * for the life of the page, and every `reel_command` would then be DROPPED by `routeReelCommand`
   * as off-stage. This is the exact bug SPEC-59 exists to fix, so the reset is total and mirrors
   * the backend: return to the reel, with no fade and no replay (the queued chapters belong to the
   * connection that died — handing them to the Projectionist now would play a dead session's film).
   */
  const resetPending = useCallback(() => {
    // The restore bookkeeping is per connection too, exactly as in `hydrate`'s null arm: left set,
    // a `selection_state` on the NEW socket naming the stage connection 1 asked about would answer
    // 'noop' instead of 'restore', and the two sides would stay diverged.
    restoreAskedRef.current = null;
    liftHoldRef.current = null; // SPEC-66: a lift-held chapter belongs to the dead connection too
    update((s) => ({
      ...applyStageCommand(s, { action: 'return_to_reel', transition: 'none' }).state,
      pendingStageCommands: [],
      pendingFilmCommands: [],
      pendingReelCommands: [],
    }));
  }, [update]);

  /**
   * SPEC-67 D-15: `resetPending` minus the `return_to_reel` apply. Called from `ws.onopen` on EVERY
   * connection, ahead of the resume guard: whatever was queued (for a fade, a film, a return, a
   * lift or a commit) belongs to the socket that died, whether or not the backend then says the
   * visit resumed (SPEC-59 AC-26). The stage, collection, piece and gallery are left alone — on a
   * resumed same-page reconnect they ARE the guest's screen, and the backend's `session_resume`
   * frame decides through `hydrate` whether they stay.
   */
  const clearPending = useCallback(() => {
    restoreAskedRef.current = null;
    liftHoldRef.current = null;
    pendingReplayRef.current = [];
    update((s) => ({
      ...s,
      returning: false,
      pendingStageCommands: [],
      pendingFilmCommands: [],
      pendingReelCommands: [],
    }));
  }, [update]);

  /** @returns {boolean} true when the caller should hand the command to the Projectionist now */
  const handleReelCommand = useCallback((msg) => {
    let verdict = 'dropped';
    update((s) => { const out = routeReelCommand(s, msg); verdict = out.verdict; return out.state; });
    if (verdict !== 'apply') return false;
    // SPEC-66: on the reel but the return's black is still lifting — wait behind its held replay.
    if (liftHoldRef.current) { liftHoldRef.current = [...liftHoldRef.current, msg]; return false; }
    // Between a swap and its commit the ref already says "reel" but the Projectionist still renders
    // off stage: queue behind the pending replay so order holds and nothing meets the stale prop.
    if (pendingReplayRef.current.length) { pendingReplayRef.current = [...pendingReplayRef.current, msg]; return false; }
    return true;
  }, [update]);

  const setGalleryIndex = useCallback((index) => {
    update((s) => {
      const count = imagesOf(s.product).length || 1;
      const i = Number.isFinite(index) ? Math.min(Math.max(0, index | 0), count - 1) : 0;
      return { ...s, galleryIndex: i };
    });
  }, [update]);

  const stepGallery = useCallback((direction) => {
    const step = direction === 'previous' ? -1 : 1;
    update((s) => {
      const count = imagesOf(s.product).length || 1;
      return { ...s, galleryIndex: (s.galleryIndex + step + count) % count };
    });
  }, [update]);

  /**
   * A hydrated `stage` domain (selection_state). Null means the server holds no stage: land on
   * the reel with no fade (server wins wholesale). Non-null and already on screen: nothing to do.
   * Otherwise the caller must ask the backend to re-dispatch (`stage_restore`).
   * @returns {'reset'|'matched'|'restore'|'noop'}
   */
  const hydrate = useCallback((value) => {
    const current = stateRef.current;
    if (!value) {
      restoreAskedRef.current = null;
      if (current.stage === Stage.REEL) return 'noop';
      // SPEC-59 D-15: the server wins wholesale, so anything still held for a fade or a film is
      // stale by definition — dropped here for the same reason `resetPending` drops it at connect.
      liftHoldRef.current = null; // SPEC-66: same for a replay held for a fade's lift
      update((s) => ({
        ...applyStageCommand(s, { action: 'return_to_reel', transition: 'none' }).state,
        pendingStageCommands: [],
        pendingFilmCommands: [],
      }));
      return 'reset';
    }
    if (stageMatches(current, value)) {
      restoreAskedRef.current = null;
      return 'matched';
    }
    // Review MEDIUM (2026-09-09): a second selection_state arriving before the backend's
    // enter_collection/open_piece land (a cart mirror mid-reconnect) must not request the same
    // restore twice — the second enter_collection would reset the card and remount it.
    const key = `${value.stage}|${value.collection ?? ''}|${value.piece_id ?? ''}`;
    if (restoreAskedRef.current === key) return 'noop';
    restoreAskedRef.current = key;
    return 'restore';
  }, [update]);

  return {
    state, stateRef, swap, handleStageCommand, handleReelCommand, setGalleryIndex, stepGallery, hydrate,
    resetPending, clearPending,
  };
}
